import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import {
  getTikTokConnection,
  startTikTokAuth,
  disconnectTikTok,
  TikTokConnection,
} from "../../../../database/community/tiktokConnection";

// First-party TikTok panel for the Media Studio. Mirrors SocialConnectPanel
// (Instagram/Postproxy) but talks to OUR TikTok app: connect via Login Kit.
// The per-project "Send to TikTok" action lives in publish/PublishButtons.

const SmallButton = styled.button`
  appearance: none;
  border: 1px solid var(--color-border-subtle);
  background: var(--color-surface);
  color: var(--color-text);
  font-family: var(--font-sans);
  font-size: 11px;
  font-weight: var(--font-weight-semibold);
  padding: 4px 10px;
  border-radius: var(--radius-pill);
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--color-accent);
    color: var(--color-accent);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled(SmallButton)`
  background: var(--color-accent);
  border-color: var(--color-accent);
  color: #fff;

  &:hover:not(:disabled) {
    color: #fff;
    filter: brightness(1.05);
  }
`;

const PanelRoot = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-2) var(--space-3);
  margin-bottom: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-lg);
  background: var(--color-surface);
`;

const PanelLabel = styled.span`
  font-family: var(--font-sans);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  margin-right: var(--space-1);
`;

const ProfileChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: var(--radius-pill);
  background: var(--color-surface-subtle);
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);

  img {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const Muted = styled.span`
  font-family: var(--font-sans);
  font-size: 11px;
  color: var(--color-text-secondary);
`;

const Spacer = styled.span`
  flex: 1 1 auto;
`;

export const TikTokConnectPanel: React.FC<{
  communityId?: string;
  onConnectedChange?: (connected: boolean) => void;
}> = ({ communityId, onConnectedChange }) => {
  const [config, setConfig] = useState<TikTokConnection | null>(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!communityId) return;
    setLoading(true);
    setError("");
    try {
      const cfg = await getTikTokConnection(communityId);
      setConfig(cfg);
      onConnectedChange?.(cfg.connected);
    } catch (e: any) {
      setError(e?.message || "Could not load TikTok status.");
    } finally {
      setLoading(false);
    }
  }, [communityId, onConnectedChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // When the coach returns from the connect popup to this tab, refresh so the
  // linked account shows up.
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const handleConnect = async () => {
    if (!communityId) return;
    setBusy(true);
    setError("");
    try {
      const { url } = await startTikTokAuth(communityId);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e: any) {
      setError(e?.message || "Could not start the TikTok connection.");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!communityId) return;
    if (!window.confirm("Disconnect this TikTok account?")) return;
    setBusy(true);
    setError("");
    try {
      await disconnectTikTok(communityId);
      await refresh();
    } catch (e: any) {
      setError(e?.message || "Could not disconnect.");
    } finally {
      setBusy(false);
    }
  };

  if (!communityId) return null;

  const connected = Boolean(config?.connected);

  return (
    <PanelRoot>
      <PanelLabel>TikTok</PanelLabel>
      {loading && !config ? (
        <Muted>Checking…</Muted>
      ) : connected ? (
        <ProfileChip>
          {config?.avatarUrl ? <img src={config.avatarUrl} alt="" /> : null}
          {config?.displayName || "Connected"}
        </ProfileChip>
      ) : (
        <Muted>No TikTok account connected yet.</Muted>
      )}
      <Spacer />
      {error ? <Muted style={{ color: "var(--color-danger)" }}>{error}</Muted> : null}
      {connected ? (
        <SmallButton type="button" onClick={handleDisconnect} disabled={busy}>
          Disconnect
        </SmallButton>
      ) : (
        <PrimaryButton type="button" onClick={handleConnect} disabled={busy}>
          {busy ? "Opening…" : "Connect TikTok"}
        </PrimaryButton>
      )}
    </PanelRoot>
  );
};

export default TikTokConnectPanel;
